import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
 ArrowLeft,
 Edit,
 Trash2,
 User,
 Car,
 DollarSign,
 Calendar,
 FileText,
 CheckCircle,
 XCircle,
 Clock,
 AlertCircle,
 Mail,
 Phone,
 MapPin,
 Tag,
 Gift,
 TrendingUp,
 TrendingDown,
} from "lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Button,
 AlertMessage,
 ConfirmDialog,
 ProposalDetailsPageSkeleton,
 Toast,
 ToastType,
} from "../../components/ui";
import { useProposals } from "../../hooks/useProposals";
import { apiService } from "../../services/api";
import { Proposal } from "../../types";
import { formatCurrency } from "../../utils/formatters";

export const ProposalDetailsPage: React.FC = () => {
 const navigate = useNavigate();
 const { id } = useParams<{ id: string }>();
 const { t } = useTranslation();
 const { approveProposal, rejectProposal } = useProposals();
 const [proposal, setProposal] = useState<Proposal | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
 const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
 const [actionType, setActionType] = useState<"accept" | "reject" | null>(null);
 const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);

 useEffect(() => {
 if (id) {
 fetchProposal(id);
 }
 }, [id]);

 const fetchProposal = async (proposalId: string) => {
 try {
 setIsLoading(true);
 setError(null);
 const response = await apiService.getProposalById(proposalId);
 setProposal(response);
 } catch (err) {
 console.error("Erro ao buscar proposta:", err);
 setError("Erro ao carregar proposta");
 } finally {
 setIsLoading(false);
 }
 };

 const handleDelete = async () => {
 if (!proposal) return;

 try {
 await apiService.deleteProposal(proposal.id);
 navigate("/proposals", {
 state: { successMessage: "Proposta excluída com sucesso!" },
 });
 } catch (error) {
 console.error("Erro ao excluir proposta:", error);
 }
 };

 const handleAccept = () => {
 setActionType("accept");
 setDecisionDialogOpen(true);
 };

 const handleReject = () => {
 setActionType("reject");
 setDecisionDialogOpen(true);
 };

 const confirmDecision = async () => {
 if (!id || !actionType) return;
 setIsSubmittingDecision(true);
 try {
 if (actionType === "accept") {
 await approveProposal(id);
 setToast({
 message: t("proposals.actions.toastAccepted"),
 type: "success",
 });
 } else {
 await rejectProposal(id);
 setToast({
 message: t("proposals.actions.toastRejected"),
 type: "success",
 });
 }
 setTimeout(() => {
 navigate("/proposals");
 }, 1000);
 } catch (err) {
 console.error(err);
 setToast({
 message:
 actionType === "accept"
 ? t("proposals.actions.toastAcceptError")
 : t("proposals.actions.toastRejectError"),
 type: "error",
 });
 } finally {
 setIsSubmittingDecision(false);
 setDecisionDialogOpen(false);
 setActionType(null);
 }
 };

 const getStatusIcon = (status: Proposal["status"] | string) => {
 switch (status) {
 case "draft":
 return <Edit className="h-5 w-5" />;
 case "sent":
 return <Clock className="h-5 w-5" />;
 case "negotiating":
 return <AlertCircle className="h-5 w-5" />;
 case "accepted":
 case "approved":
 return <CheckCircle className="h-5 w-5" />;
 case "rejected":
 return <XCircle className="h-5 w-5" />;
 case "expired":
 return <Clock className="h-5 w-5" />;
 case "cancelled":
 return <XCircle className="h-5 w-5" />;
 default:
 return <Clock className="h-5 w-5" />;
 }
 };

 const getStatusLabel = (status: Proposal["status"] | string) => {
 const labels: Record<string, string> = {
 draft: "Rascunho",
 sent: "Enviada",
 negotiating: "Negociando",
 accepted: "Aceita",
 approved: "Aprovada",
 rejected: "Rejeitada",
 expired: "Expirada",
 cancelled: "Cancelada",
 };
 return labels[status] || status;
 };

 const getStatusColor = (status: Proposal["status"] | string) => {
 switch (status) {
 case "draft":
 return "bg-surface text-foreground";
 case "sent":
 return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
 case "negotiating":
 return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
 case "accepted":
 case "approved":
 return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case "rejected":
 return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 case "expired":
 return "bg-surface text-foreground";
 case "cancelled":
 return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 default:
 return "bg-surface text-foreground";
 }
 };

 const canEditProposal = (status: Proposal["status"] | string) => {
 const nonEditableStatuses = ["accepted", "approved", "rejected", "expired"];
 return !nonEditableStatuses.includes(status);
 };

 if (isLoading) {
 return <ProposalDetailsPageSkeleton />;
 }

 if (error || !proposal) {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <Button
 variant="outline"
 onClick={() => navigate("/proposals")}
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 Voltar
 </Button>
 <h1 className="text-2xl font-bold text-foreground">
 Visualizar Proposta
 </h1>
 </div>
 <AlertMessage
 variant="error"
 message={error || "Proposta não encontrada"}
 />
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div className="flex items-center gap-4">
 <Button
 variant="outline"
 onClick={() => navigate("/proposals")}
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 Voltar
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-foreground">
 Proposta #{proposal.id.substring(0, 8)}
 </h1>
 <p className="text-sm text-muted-foreground mt-1">
 Criada em{" "}
 {new Date(proposal.createdAt).toLocaleDateString("pt-BR", {
 day: "2-digit",
 month: "long",
 year: "numeric",
 hour: "2-digit",
 minute: "2-digit",
 })}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <span
 className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-medium ${getStatusColor(
 proposal.status
 )}`}
 >
 {getStatusIcon(proposal.status)}
 {getStatusLabel(proposal.status)}
 </span>
 {canEditProposal(proposal.status) && (
 <>
 <Button
 variant="outline"
 onClick={handleReject}
 disabled={isSubmittingDecision}
 className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
 >
 <XCircle className="h-4 w-4" />
 {t("proposals.actions.rejectProposal")}
 </Button>
 <Button
 onClick={handleAccept}
 disabled={isSubmittingDecision}
 className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
 >
 <CheckCircle className="h-4 w-4" />
 {t("proposals.actions.acceptProposal")}
 </Button>
 <Button
 variant="outline"
 onClick={() => {
 navigate("/proposals", {
 state: { editProposalId: proposal.id },
 });
 }}
 >
 <Edit className="h-4 w-4 mr-2" />
 Editar
 </Button>
 </>
 )}
 <Button
 variant="outline"
 onClick={() => setDeleteDialogOpen(true)}
 className="text-red-600 hover:text-red-700 hover:border-red-300"
 >
 <Trash2 className="h-4 w-4 mr-2" />
 Excluir
 </Button>
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 {/* Conteúdo Principal */}
 <div className="lg:col-span-2 space-y-6">
 {/* Informações do Cliente */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <User className="h-5 w-5 text-accent" />
 Cliente
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div>
 <h3 className="font-semibold text-foreground text-lg">
 {proposal.lead.name}
 </h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {proposal.lead.email && (
 <div className="flex items-center gap-2 text-muted-foreground">
 <Mail className="h-4 w-4" />
 <span>{proposal.lead.email}</span>
 </div>
 )}
 {proposal.lead.phone && (
 <div className="flex items-center gap-2 text-muted-foreground">
 <Phone className="h-4 w-4" />
 <span>{proposal.lead.phone}</span>
 </div>
 )}
 {proposal.lead.phones && proposal.lead.phones.length > 0 && (
 <div className="flex flex-col gap-1">
 {proposal.lead.phones.map((phone, index) => (
 <div
 key={index}
 className="flex items-center gap-2 text-muted-foreground"
 >
 <Phone className="h-4 w-4" />
 <span>
 {(phone as any).area} {phone.phone}
 </span>
 {phone.isPrimary && (
 <span className="text-xs text-accent">
 (Principal)
 </span>
 )}
 </div>
 ))}
 </div>
 )}
 {proposal.lead.emails && proposal.lead.emails.length > 0 && (
 <div className="flex flex-col gap-1">
 {proposal.lead.emails.map((email, index) => (
 <div
 key={index}
 className="flex items-center gap-2 text-muted-foreground"
 >
 <Mail className="h-4 w-4" />
 <span>{email.email}</span>
 {email.isPrimary && (
 <span className="text-xs text-accent">
 (Principal)
 </span>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 <div className="pt-4 border-t border-border">
 <Button
 variant="outline"
 onClick={() =>
 navigate(`/leads/edit/${proposal.leadId}`)
 }
 className="w-full sm:w-auto"
 >
 Ver Detalhes do Lead
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Informações do Veículo */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Car className="h-5 w-5 text-accent" />
 Veículo
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 {proposal.vehicle.photos && proposal.vehicle.photos[0] && (
 <div className="w-full h-64 rounded-lg overflow-hidden bg-surface">
 <img
 src={proposal.vehicle.photos[0].url}
 alt={proposal.vehicle.name}
 className="w-full h-full object-cover"
 />
 </div>
 )}
 <div>
 <h3 className="font-semibold text-foreground text-lg mb-2">
 {(proposal.vehicle.brand as any)?.name || proposal.vehicle.brand}{" "}
 {(proposal.vehicle.model as any)?.name || proposal.vehicle.model}{" "}
 {(proposal.vehicle as any).year || (proposal.vehicle as any).modelYear}
 </h3>
 <p className="text-sm text-muted-foreground">
 {proposal.vehicle.name}
 </p>
 </div>
 <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
 <div>
 <p className="text-sm text-muted-foreground">
 Preço Original
 </p>
 <p className="font-semibold text-foreground">
 {formatCurrency(proposal.vehicle.salePrice)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground">
 Ano
 </p>
 <p className="font-semibold text-foreground">
 {proposal.vehicle.year || (proposal.vehicle as any).modelYear}
 </p>
 </div>
 </div>
 <div className="pt-4 border-t border-border">
 <Button
 variant="outline"
 onClick={() =>
 navigate(`/vehicles/${proposal.vehicleId}`)
 }
 className="w-full sm:w-auto"
 >
 Ver Detalhes do Veículo
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Detalhes da Proposta */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <FileText className="h-5 w-5 text-accent" />
 Detalhes da Proposta
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <p className="text-sm text-muted-foreground mb-1">
 Preço Proposto
 </p>
 <p className="font-semibold text-lg text-foreground">
 {formatCurrency(proposal.proposedPrice)}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground mb-1">
 Valor Final
 </p>
 <p className="font-semibold text-lg text-accent">
 {formatCurrency(
 proposal.finalValue || proposal.proposedPrice
 )}
 </p>
 </div>
 <div>
 <p className="text-sm text-muted-foreground mb-1">
 Preço Original
 </p>
 <p className="font-semibold text-foreground">
 {formatCurrency(proposal.originalPrice)}
 </p>
 </div>
 {proposal.discount && proposal.discount > 0 && (
 <div>
 <p className="text-sm text-muted-foreground mb-1">
 Desconto
 </p>
 <p className="font-semibold text-green-600">
 {formatCurrency(proposal.discount)}
 {proposal.discountPercentage && (
 <span className="ml-2 text-sm">
 ({proposal.discountPercentage.toFixed(1)}%)
 </span>
 )}
 </p>
 </div>
 )}
 </div>

 {proposal.validUntil && (
 <div className="pt-4 border-t border-border">
 <div className="flex items-center gap-2 text-muted-foreground">
 <Calendar className="h-4 w-4" />
 <span className="text-sm">Válido até:</span>
 <span className="font-medium text-foreground">
 {new Date(proposal.validUntil).toLocaleDateString(
 "pt-BR",
 {
 day: "2-digit",
 month: "long",
 year: "numeric",
 }
 )}
 </span>
 </div>
 </div>
 )}

 {proposal.deliveryDate && (
 <div className="pt-4 border-t border-border">
 <div className="flex items-center gap-2 text-muted-foreground">
 <Calendar className="h-4 w-4" />
 <span className="text-sm">Data de Entrega:</span>
 <span className="font-medium text-foreground">
 {new Date(proposal.deliveryDate).toLocaleDateString(
 "pt-BR",
 {
 day: "2-digit",
 month: "long",
 year: "numeric",
 }
 )}
 </span>
 </div>
 </div>
 )}

 {proposal.paymentMethod && (
 <div className="pt-4 border-t border-border">
 <p className="text-sm text-muted-foreground mb-1">
 Forma de Pagamento
 </p>
 <p className="font-medium text-foreground">
 {proposal.paymentMethod}
 </p>
 </div>
 )}

 {proposal.financingOptions && (
 <div className="pt-4 border-t border-border">
 <p className="text-sm text-muted-foreground mb-1">
 Opções de Financiamento
 </p>
 <p className="font-medium text-foreground">
 {proposal.financingOptions}
 </p>
 </div>
 )}

 {proposal.terms && (
 <div className="pt-4 border-t border-border">
 <p className="text-sm text-muted-foreground mb-2">
 Termos e Condições
 </p>
 <p className="text-sm text-foreground whitespace-pre-wrap">
 {proposal.terms}
 </p>
 </div>
 )}

 {proposal.notes && (
 <div className="pt-4 border-t border-border">
 <p className="text-sm text-muted-foreground mb-2">
 Observações
 </p>
 <p className="text-sm text-foreground whitespace-pre-wrap">
 {proposal.notes}
 </p>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Benefícios */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Gift className="h-5 w-5 text-accent" />
 Benefícios
 </CardTitle>
 </CardHeader>
 <CardContent>
 {!proposal.benefitUsages ||
 proposal.benefitUsages.length === 0 ? (
 <p className="text-sm text-muted-foreground">
 Nenhum benefício aplicado nesta proposta
 </p>
 ) : (
 <div className="space-y-3">
 {proposal.benefitUsages.map((benefitUsage) => {
 const benefitValue = parseFloat(benefitUsage.value) || 0;
 const originalValue = benefitUsage.benefit?.value || 0;
 const hasDiscount = benefitValue < originalValue;

 return (
 <div
 key={benefitUsage.id}
 className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
 >
 <div className="flex items-center gap-3 flex-1">
 <Gift className="h-5 w-5 text-green-600 dark:text-green-400" />
 <div>
 <p className="font-medium text-foreground">
 {benefitUsage.benefit?.name || "Benefício"}
 </p>
 {hasDiscount && originalValue > 0 && (
 <p className="text-xs text-muted-foreground">
 Valor original: {formatCurrency(originalValue)}
 </p>
 )}
 </div>
 </div>
 <div className="flex flex-col items-end">
 <div className="flex items-center gap-2">
 {hasDiscount && originalValue > 0 && (
 <span className="text-sm text-muted-foreground line-through">
 {formatCurrency(originalValue)}
 </span>
 )}
 <span className="font-semibold text-green-600 dark:text-green-400">
 {formatCurrency(benefitValue)}
 </span>
 </div>
 {hasDiscount && originalValue > 0 && (
 <span className="text-xs text-green-600 dark:text-green-400">
 Economia:{" "}
 {formatCurrency(originalValue - benefitValue)}
 </span>
 )}
 </div>
 </div>
 );
 })}
 <div className="pt-3 border-t border-border">
 <div className="flex justify-between items-center">
 <span className="font-medium text-foreground">
 Total de Benefícios:
 </span>
 <span className="text-lg font-bold text-green-600 dark:text-green-400">
 {formatCurrency(
 proposal.benefitUsages.reduce(
 (sum, b) => sum + (parseFloat(b.value) || 0),
 0
 )
 )}
 </span>
 </div>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Encargos Financeiros */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <AlertCircle className="h-5 w-5 text-amber-600" />
 Encargos Financeiros
 </CardTitle>
 </CardHeader>
 <CardContent>
 {!proposal.chargeUsages || proposal.chargeUsages.length === 0 ? (
 <p className="text-sm text-muted-foreground">
 Nenhum encargo aplicado nesta proposta
 </p>
 ) : (
 <div className="space-y-3">
 {proposal.chargeUsages.map((chargeUsage) => {
 const chargeValue = parseFloat(chargeUsage.value) || 0;
 const charge = chargeUsage.charge;
 const discountPercentage = chargeUsage.discountPercentage
 ? typeof chargeUsage.discountPercentage === "string"
 ? parseFloat(chargeUsage.discountPercentage)
 : chargeUsage.discountPercentage
 : 0;
 let originalValue = 0;

 if (charge) {
 if (charge.isPercentage) {
 // Para encargos percentuais, precisamos calcular o valor original
 // baseado no preço proposto + benefícios
 const basePrice = proposal.proposedPrice || 0;
 const benefitsTotal = (
 proposal.benefitUsages || []
 ).reduce(
 (sum, b) => sum + (parseFloat(b.value) || 0),
 0
 );
 const priceForPercentage = basePrice + benefitsTotal;
 originalValue =
 (priceForPercentage * (charge.percentage || 0)) / 100;
 } else {
 originalValue = charge.amount || 0;
 }
 }

 const hasDiscount = discountPercentage > 0;
 const discountValue =
 hasDiscount && originalValue > 0
 ? (originalValue * discountPercentage) / 100
 : 0;

 return (
 <div
 key={chargeUsage.id}
 className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
 >
 <div className="flex items-center gap-3 flex-1">
 <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
 <div>
 <p className="font-medium text-foreground">
 {charge?.name || "Encargo"}
 </p>
 {hasDiscount && (
 <p className="text-xs text-muted-foreground">
 Desconto: {discountPercentage.toFixed(1)}%
 </p>
 )}
 </div>
 </div>
 <div className="flex flex-col items-end">
 <div className="flex items-center gap-2">
 {hasDiscount && originalValue > 0 && (
 <span className="text-sm text-muted-foreground line-through">
 {formatCurrency(originalValue)}
 </span>
 )}
 <span className="font-semibold text-amber-600 dark:text-amber-400">
 {formatCurrency(chargeValue)}
 </span>
 </div>
 {discountValue > 0 && (
 <span className="text-xs text-green-600 dark:text-green-400">
 Economia: {formatCurrency(discountValue)}
 </span>
 )}
 </div>
 </div>
 );
 })}
 <div className="pt-3 border-t border-border">
 <div className="flex justify-between items-center">
 <span className="font-medium text-foreground">
 Total de Encargos:
 </span>
 <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
 {formatCurrency(
 proposal.chargeUsages.reduce(
 (sum, c) => sum + (parseFloat(c.value) || 0),
 0
 )
 )}
 </span>
 </div>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Veículos de Troca */}
 {proposal.tradeVehicles && proposal.tradeVehicles.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Car className="h-5 w-5 text-purple-600" />
 Veículos de Troca
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {proposal.tradeVehicles.map((tradeVehicle, index) => {
 const brandName = (tradeVehicle as any).brand?.name || "Marca não informada";
 const modelName = (tradeVehicle as any).model?.name || "Modelo não informado";
 const versionName = (tradeVehicle as any).version?.name;
 const conditionLabel =
 tradeVehicle.condition === "used"
 ? "Usado"
 : tradeVehicle.condition === "new"
 ? "Novo"
 : tradeVehicle.condition;
 const fuelTypeLabel =
 tradeVehicle.fuelType === "flex"
 ? "Flex"
 : tradeVehicle.fuelType === "gasoline"
 ? "Gasolina"
 : tradeVehicle.fuelType === "ethanol"
 ? "Etanol"
 : tradeVehicle.fuelType === "diesel"
 ? "Diesel"
 : tradeVehicle.fuelType === "electric"
 ? "Elétrico"
 : tradeVehicle.fuelType;
 const transmissionLabel =
 tradeVehicle.transmission === "manual"
 ? "Manual"
 : tradeVehicle.transmission === "automatic"
 ? "Automático"
 : tradeVehicle.transmission;

 return (
 <div
 key={index}
 className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg"
 >
 <div className="flex items-start justify-between mb-3">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <Car className="h-5 w-5 text-purple-600 dark:text-purple-400" />
 <h4 className="font-semibold text-foreground">
 {brandName} {modelName}
 {versionName && ` - ${versionName}`}
 </h4>
 </div>
 <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
 <div>
 <span className="font-medium">Ano Modelo:</span>{" "}
 {tradeVehicle.modelYear}
 </div>
 <div>
 <span className="font-medium">Ano Fabricação:</span>{" "}
 {tradeVehicle.manufactureYear}
 </div>
 <div>
 <span className="font-medium">Quilometragem:</span>{" "}
 {tradeVehicle.mileage.toLocaleString("pt-BR")} km
 </div>
 <div>
 <span className="font-medium">Condição:</span> {conditionLabel}
 </div>
 <div>
 <span className="font-medium">Combustível:</span> {fuelTypeLabel}
 </div>
 <div>
 <span className="font-medium">Transmissão:</span>{" "}
 {transmissionLabel}
 </div>
 {tradeVehicle.color && (
 <div>
 <span className="font-medium">Cor:</span> {tradeVehicle.color}
 </div>
 )}
 {tradeVehicle.plate && (
 <div>
 <span className="font-medium">Placa:</span> {tradeVehicle.plate}
 </div>
 )}
 </div>
 {(tradeVehicle as any).expenses &&
 (tradeVehicle as any).expenses.length > 0 && (
 <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
 <p className="text-sm font-medium text-foreground mb-2">
 Gastos Adicionais:
 </p>
 <div className="space-y-1">
 {(tradeVehicle as any).expenses.map(
 (expense: any, expIndex: number) => (
 <div
 key={expIndex}
 className="flex justify-between text-sm text-muted-foreground"
 >
 <span>{expense.name}</span>
 <span>{formatCurrency(expense.value)}</span>
 </div>
 )
 )}
 </div>
 </div>
 )}
 </div>
 <div className="ml-4 text-right">
 <p className="text-sm text-muted-foreground mb-1">
 Valor Oferecido
 </p>
 <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
 {formatCurrency(tradeVehicle.offeredValue)}
 </p>
 </div>
 </div>
 </div>
 );
 })}
 <div className="pt-3 border-t border-border">
 <div className="flex justify-between items-center">
 <span className="font-medium text-foreground">
 Total em Veículos de Troca:
 </span>
 <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
 {formatCurrency(
 proposal.tradeVehicles.reduce(
 (sum, tv) => sum + (tv.offeredValue || 0),
 0
 )
 )}
 </span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Resumo Financeiro */}
 {(proposal.benefitUsages && proposal.benefitUsages.length > 0) ||
 (proposal.chargeUsages && proposal.chargeUsages.length > 0) ? (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5 text-accent" />
 Resumo Financeiro
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 <div className="flex justify-between items-center text-muted-foreground">
 <span>Valor do Veículo Negociado:</span>
 <span className="font-medium text-foreground">
 {formatCurrency(proposal.originalPrice ?? 0)}
 </span>
 </div>
 <div className="flex justify-between items-center text-muted-foreground">
 <span>Preço Proposto:</span>
 <span className="font-medium text-foreground">
 {formatCurrency(proposal.value ?? 0)}
 </span>
 </div>
 {proposal.benefitUsages &&
 proposal.benefitUsages.length > 0 && (
 <div className="flex justify-between items-center text-muted-foreground">
 <span className="flex items-center gap-2">
 <TrendingUp className="h-4 w-4 text-green-600" />
 (+) Benefícios:
 </span>
 <span className="font-medium text-green-600">
 +{" "}
 {formatCurrency(
 proposal.benefitUsages.reduce(
 (sum, b) => sum + (parseFloat(b.value) || 0),
 0
 )
 )}
 </span>
 </div>
 )}
 {proposal.chargeUsages &&
 proposal.chargeUsages.length > 0 && (
 <div className="flex justify-between items-center text-muted-foreground">
 <span className="flex items-center gap-2">
 <TrendingDown className="h-4 w-4 text-amber-600" />
 (+) Encargos:
 </span>
 <span className="font-medium text-amber-600">
 +{" "}
 {formatCurrency(
 proposal.chargeUsages.reduce(
 (sum, c) => sum + (parseFloat(c.value) || 0),
 0
 )
 )}
 </span>
 </div>
 )}
 {proposal.tradeVehicles &&
 proposal.tradeVehicles.length > 0 && (
 <div className="flex justify-between items-center text-muted-foreground">
 <span className="flex items-center gap-2">
 <Car className="h-4 w-4 text-purple-600" />
 (-) Veículos de Troca:
 </span>
 <span className="font-medium text-purple-600">
 -{" "}
 {formatCurrency(
 proposal.tradeVehicles.reduce(
 (sum, tv) => sum + (tv.offeredValue || 0),
 0
 )
 )}
 </span>
 </div>
 )}
 <div className="pt-3 border-t-2 border-border">
 <div className="flex justify-between items-center">
 <span className="text-lg font-semibold text-foreground">
 Valor Final:
 </span>
 <span className="text-2xl font-bold text-accent dark:text-accent">
 {formatCurrency(
 proposal.finalValue || proposal.proposedPrice
 )}
 </span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 ) : null}

 {/* Pagamentos */}
 {proposal.payments && proposal.payments.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5 text-accent" />
 Formas de Pagamento
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {proposal.payments.map((payment) => {
 const paymentFormName =
 payment.paymentForm?.name || "Pagamento";
 const paymentFormDescription =
 payment.paymentForm?.description;
 const paymentOptionName = payment.paymentOption?.name;

 return (
 <div
 key={payment.id}
 className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
 >
 <div className="flex items-center gap-3 flex-1">
 <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
 <div>
 <p className="font-medium text-foreground">
 {paymentFormName}
 {paymentOptionName && (
 <span className="ml-2 text-sm text-muted-foreground">
 - {paymentOptionName}
 </span>
 )}
 </p>
 {paymentFormDescription && (
 <p className="text-xs text-muted-foreground">
 {paymentFormDescription}
 </p>
 )}
 {payment.installments > 1 && (
 <p className="text-xs text-muted-foreground">
 {payment.installments}x de{" "}
 {formatCurrency(
 parseFloat(payment.totalValue) /
 payment.installments
 )}
 </p>
 )}
 {payment.interestRate && (
 <p className="text-xs text-muted-foreground">
 Taxa de juros:{" "}
 {parseFloat(payment.interestRate).toFixed(2)}%
 </p>
 )}
 </div>
 </div>
 <div className="flex flex-col items-end">
 <span className="font-semibold text-blue-600 dark:text-blue-400">
 {formatCurrency(parseFloat(payment.totalValue))}
 </span>
 {payment.downPayment &&
 parseFloat(payment.downPayment) > 0 && (
 <span className="text-xs text-muted-foreground">
 Entrada:{" "}
 {formatCurrency(
 parseFloat(payment.downPayment)
 )}
 </span>
 )}
 </div>
 </div>
 );
 })}
 <div className="pt-3 border-t border-border">
 <div className="flex justify-between items-center">
 <span className="font-medium text-foreground">
 Total em Pagamentos:
 </span>
 <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
 {formatCurrency(
 proposal.payments.reduce(
 (sum, p) => sum + (parseFloat(p.totalValue) || 0),
 0
 )
 )}
 </span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}
 </div>

 {/* Sidebar */}
 <div className="space-y-6">
 {/* Valor Final */}
 <Card>
 <CardHeader>
 <CardTitle className="text-3xl font-bold text-accent dark:text-accent">
 {formatCurrency(proposal.finalValue || proposal.proposedPrice)}
 </CardTitle>
 <p className="text-sm text-muted-foreground">
 Valor Final da Proposta
 </p>
 </CardHeader>
 <CardContent className="space-y-3">
 {proposal.discount && proposal.discount > 0 && (
 <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
 <p className="text-sm font-medium text-green-800 dark:text-green-200">
 Desconto: {formatCurrency(proposal.discount)}
 {proposal.discountPercentage && (
 <span className="ml-1">
 ({proposal.discountPercentage.toFixed(1)}%)
 </span>
 )}
 </p>
 </div>
 )}
 {canEditProposal(proposal.status) && (
 <Button
 className="w-full"
 size="lg"
 onClick={() => {
 navigate("/proposals", {
 state: { editProposalId: proposal.id },
 });
 }}
 >
 <Edit className="h-4 w-4 mr-2" />
 Editar Proposta
 </Button>
 )}
 </CardContent>
 </Card>

 {/* Informações Adicionais */}
 <Card>
 <CardHeader>
 <CardTitle>Informações Adicionais</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 {proposal.assignedUser && (
 <div>
 <p className="text-sm text-muted-foreground mb-1">
 Vendedor
 </p>
 <div className="flex items-center gap-2">
 <User className="h-4 w-4 text-muted-foreground" />
 <p className="font-medium text-foreground">
 {proposal.assignedUser.name}
 </p>
 </div>
 {proposal.assignedUser.email && (
 <p className="text-sm text-muted-foreground ml-6">
 {proposal.assignedUser.email}
 </p>
 )}
 </div>
 )}

 <div className="pt-4 border-t border-border">
 <p className="text-sm text-muted-foreground mb-2">
 Datas
 </p>
 <div className="space-y-2">
 <div className="flex items-center gap-2 text-sm text-muted-foreground">
 <Calendar className="h-4 w-4" />
 <span>
 Criada:{" "}
 {new Date(proposal.createdAt).toLocaleDateString("pt-BR")}
 </span>
 </div>
 <div className="flex items-center gap-2 text-sm text-muted-foreground">
 <Calendar className="h-4 w-4" />
 <span>
 Atualizada:{" "}
 {new Date(proposal.updatedAt).toLocaleDateString("pt-BR")}
 </span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Delete Confirmation Dialog */}
 <ConfirmDialog
 isOpen={deleteDialogOpen}
 title="Confirmar Exclusão"
 description={`Tem certeza que deseja excluir esta proposta? Esta ação não pode ser desfeita.`}
 onConfirm={handleDelete}
 onClose={() => setDeleteDialogOpen(false)}
 />
 <ConfirmDialog
 isOpen={decisionDialogOpen}
 title={
 actionType === "accept"
 ? t("proposals.actions.dialogAcceptTitle")
 : t("proposals.actions.dialogRejectTitle")
 }
 description={
 actionType === "accept"
 ? t("proposals.actions.dialogAcceptBody")
 : t("proposals.actions.dialogRejectBody")
 }
 onConfirm={confirmDecision}
 onClose={() => {
 setDecisionDialogOpen(false);
 setActionType(null);
 }}
 type={actionType === "accept" ? "success" : "danger"}
 confirmText={
 actionType === "accept"
 ? t("proposals.actions.confirmAccept")
 : t("proposals.actions.confirmReject")
 }
 isLoading={isSubmittingDecision}
 />
 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 );
};
